import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  collection,
  addDoc,
  increment,
  updateDoc,
  serverTimestamp,
  query,
  where,
  Timestamp,
  orderBy,
  getDocs
} from 'firebase/firestore';
import {
  getToken,
  onMessage
} from 'firebase/messaging';
import {
  getFunctions,
  httpsCallable
} from 'firebase/functions';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const functions = getFunctions(app);

const getApiKey = async () => {
  try {
    const docRef = doc(db, 'keys', 'apikeys');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data().apikey;
    } else {
      console.warn("API Key document not found.");
      return null;
    }
  } catch (error) {
    console.error('Error fetching API key:', error);
    return null;
  }
};

/**
 * Fetches the Secret Key from Firestore.
 */
const getSecretKey = async () => {
  try {
    const docRef = doc(db, 'keys', 'apikeys');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data().secretKey;
    } else {
      console.warn("Secret Key document not found.");
      return null;
    }
  } catch (error) {
    console.error('Error fetching Secret key:', error);
    return null;
  }
};

/**
 * Fetches the Envia Token from Firestore.
 */
const getEnviaToken = async () => {
  try {
    const docRef = doc(db, 'keys', 'enviatoken');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data().enviatoken;
    } else {
      console.warn("Envia Token document not found.");
      return null;
    }
  } catch (error) {
    console.error('Error fetching Envia Token:', error);
    return null;
  }
};
const recordVisit = async () => {
  try {
    const visitDocRef = doc(db, 'analytics', 'visit');
    const visitDocSnap = await getDoc(visitDocRef);

    if (!visitDocSnap.exists()) {
      await setDoc(visitDocRef, { initialized: true });
    }

    const sitevisitCollection = collection(visitDocRef, 'sitevisit');
    await addDoc(sitevisitCollection, {
      timestamp: Timestamp.now(),
    });
  } catch (error) {
    console.error('❌ Error recording visit:', error);
  }
};

//product Analytics
const clickedProduct = async (productId) => {
  const id = String(productId); // 🔁 Ensure it's a string

  if (!id) {
    console.error('❌ Invalid productId:', productId);
    return;
  }

  try {
    const productDocRef = doc(db, 'analytics', 'products');
    const userProductDocRef = doc(productDocRef, 'userProducts', id); // Use the string ID

    const snap = await getDoc(userProductDocRef);

    if (snap.exists()) {
      await updateDoc(userProductDocRef, {
        count: increment(1),
        lastClicked: new Date(),
      });
    } else {
      await setDoc(userProductDocRef, {
        count: 1,
        created: new Date(),
        lastClicked: new Date(),
      });
    }
  } catch (error) {
    console.error('❌ Error recording product click:', error);
  }
};
const getProductAnalytics = async () => {
  try {
    const productAnalyticsRef = collection(db, 'analytics', 'products', 'userProducts');
    const snapshot = await getDocs(productAnalyticsRef);

    const result = {};

    snapshot.forEach((doc) => {
      const data = doc.data();
      result[doc.id] = {
        count: data.count || 0,
        created: data.created?.toDate() || null,
        lastClicked: data.lastClicked?.toDate() || null,
      };
    });

    return result;
  } catch (error) {
    console.error('❌ Error getting product analytics:', error);
    return {};
  }
};
//end product analytics

const getVisitCounts = async (period = 'day') => {
  try {
    const now = Timestamp.now().toDate();
    let start;

    switch (period) {
      case 'day':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'week':
        start = new Date(now);
        start.setDate(now.getDate() - 6);
        break;
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'year':
        start = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    }

    const visitDocRef = doc(db, 'analytics', 'visit');
    const sitevisitRef = collection(visitDocRef, 'sitevisit');
    const q = query(sitevisitRef, where('timestamp', '>=', Timestamp.fromDate(start)), orderBy('timestamp'));
    const snapshot = await getDocs(q);

    const grouped = {};
    snapshot.forEach(doc => {
      const ts = doc.data().timestamp.toDate();
      let key = '';

      if (period === 'day') {
        key = ts.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
      } else if (period === 'week') {
        key = ts.toLocaleDateString('en-US', { weekday: 'short' });
      } else if (period === 'month') {
        key = ts.toLocaleDateString('en-US', { day: 'numeric' }); // e.g., 1, 2, 3...
      } else if (period === 'year') {
        key = ts.toLocaleDateString('en-US', { month: 'short' }); // Jan, Feb, ...
      }

      grouped[key] = (grouped[key] || 0) + 1;
    });

    // Sort keys
    const ordered = Object.fromEntries(
      Object.entries(grouped).sort((a, b) => {
        return a[0].localeCompare(b[0], undefined, { numeric: true });
      })
    );

    return { total: snapshot.size, grouped: ordered };
  } catch (error) {
    console.error('❌ Error fetching visit counts:', error);
    return { total: 0, grouped: {} };
  }
};

// Fetch products from Firestore
const fetchProducts = async () => {
  try {
    const productSnapshot = await getDocs(collection(db, 'products'));
    return productSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error fetching products:', error);
    return [];
  }
};

// Fetch specifications from Firestore
const fetchSpecifications = async () => {
  try {
    const specSnapshot = await getDocs(collection(db, 'specifications'));
    return specSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Error fetching specifications:', error);
    return [];
  }
};

const addProduct = async (product, specification) => {
  try {
    // Use the provided product.id as the document ID
    const productId = product.id.toString();

    // Add product to 'products' collection with the specified ID
    await setDoc(doc(db, 'products', productId), product);

    // Add specification to 'specifications' collection with the same ID
    const specData = { id: product.id, ...specification };
    await setDoc(doc(db, 'specifications', productId), specData);

    return {
      id: productId,
      ...product,
      specification: specData,
    };
  } catch (error) {
    console.error('Error adding product or specification:', error);
    throw error;
  }
};
export {
  auth,
  db,
  app,
  doc,
  setDoc,
  collection,
  addDoc,
  getToken,
  onMessage,
  recordVisit,
  getVisitCounts,
  getApiKey,
  getSecretKey,
  getEnviaToken,
  clickedProduct,
  getProductAnalytics,
  fetchProducts,
  addProduct,
  fetchSpecifications
};
